import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../utils/cn";

const inputVariants = cva(
  "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex w-full min-w-0 rounded-md border bg-background px-3 py-1 text-base transition-all outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      inputSize: {
        default: "h-10",
        sm: "h-8 text-xs",
        lg: "h-12 text-lg",
      },
      error: {
        true: "border-destructive focus-visible:ring-destructive/20 focus-visible:border-destructive",
      },
    },
    defaultVariants: {
      inputSize: "default",
    },
  },
);

type InputVariantProps = VariantProps<typeof inputVariants>;

export interface InputProps extends Omit<
  React.ComponentPropsWithoutRef<"input">,
  "size"
> {
  /** 입력창의 크기를 결정합니다. */
  inputSize?: InputVariantProps["inputSize"];
  /** 에러 상태 여부를 결정합니다. */
  error?: boolean;
  /** 입력창 왼쪽에 표시할 아이콘입니다. */
  leftIcon?: React.ReactNode;
  /** 입력창 오른쪽에 표시할 아이콘입니다. */
  rightIcon?: React.ReactNode;
  /** 컨테이너에 추가할 Tailwind 클래스입니다. */
  containerClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      type,
      inputSize,
      error,
      leftIcon,
      rightIcon,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        className={cn("relative flex w-full items-center", containerClassName)}
      >
        {leftIcon && (
          <div className="absolute left-3 flex items-center justify-center text-muted-foreground pointer-events-none [&_svg]:size-4">
            {leftIcon}
          </div>
        )}
        <input
          type={type}
          ref={ref}
          data-slot="input"
          className={cn(
            inputVariants({ inputSize, error, className }),
            leftIcon && "pl-10",
            rightIcon && "pr-10",
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 flex items-center justify-center text-muted-foreground [&_svg]:size-4">
            {rightIcon}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input, inputVariants };
